import {
  Dispatch,
  FC,
  SetStateAction,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Stack, Tab, Tabs } from '@mui/material';

import { useQuery } from '@tanstack/react-query';

import { PageResponseUserInfo } from '@/api/data-contracts';
import CommentOrTaskForm from '@/features/comments/ui/CommentOrTaskForm';
import Comment from '@/features/comments/ui/Comments/Comment';
import { CommentsList } from '@/features/comments/ui/Comments/Comments.styles';
import EmptyState from '@/features/comments/ui/Comments/EmptyState';
import NewCommentsDivider from '@/features/comments/ui/Comments/NewCommentsDivider/NewCommentsDivider';
import { CommentWithUnread } from '@/features/comments/ui/Comments/commentUtils';
import useComments from '@/features/comments/ui/Comments/useComments';
import { useOrdersService } from '@/features/orders';
import { useUsersService } from '@/features/users';
import { queryKeys } from '@/react-query/constants';

interface Props {
  mediaPlanId?: string;
  orderId?: string;
  locationId?: string;
  taskId?: string;
  attachmentId?: string;
  commentsThreadId?: string;
  setLocationId?: Dispatch<SetStateAction<string | undefined>>;
  onTaskSave?: (content: string, priority?: 'LOW' | 'MEDIUM' | 'HIGH') => void;
  onUnreadCountChange?: (count: number) => void;
}

const Comments: FC<Props> = ({
  orderId,
  locationId,
  taskId,
  mediaPlanId,
  attachmentId,
  commentsThreadId,
  setLocationId,
  onTaskSave,
  onUnreadCountChange,
}) => {
  const { t } = useTranslation();
  const { getAllUsers } = useUsersService();
  const { getOrderUsersForComments } = useOrdersService();

  const { commentThreads, saveComment, deleteComment, tabs, isLoading, downloadCommentFile } =
    useComments({
      orderId,
      locationId,
      mediaPlanId,
      taskId,
      attachmentId,
    });

  useEffect(() => {
    if (tabs && tabs.length > 0 && !locationId && setLocationId) {
      setLocationId(tabs[0].locationId);
    }
  }, [locationId, setLocationId, tabs]);

  const { data: organizationUsers } = useQuery<PageResponseUserInfo>(
    [queryKeys.user, 'all'],
    ({ signal }) =>
      getAllUsers(
        {
          request: { pageNumber: 0, numberOfItems: 1001 },
        },
        signal,
      ),
    { enabled: !(orderId && locationId) },
  );

  const { data: orderUsers } = useQuery<PageResponseUserInfo>(
    [queryKeys.user, 'order', orderId, locationId],
    ({ signal }) =>
      getOrderUsersForComments(
        orderId!,
        locationId!,
        { pageNumber: 0, numberOfItems: 1001 },
        signal,
      ),
    { enabled: !!orderId && !!locationId },
  );

  const allUsers = useMemo(() => {
    if (orderId && locationId) {
      return orderUsers;
    }

    return organizationUsers;
  }, [orderId, locationId, organizationUsers, orderUsers]);

  const unreadCount = useMemo(() => {
    if (!commentThreads) return 0;
    return Object.values(commentThreads)
      .flat()
      .filter((c) => !c.parentId && c.isUnread).length;
  }, [commentThreads]);

  const firstUnreadCommentId = useMemo(() => {
    if (!commentThreads) return undefined;
    for (const [, comments] of Object.entries(commentThreads).reverse()) {
      const rootUnread = comments.find((c: CommentWithUnread) => !c.parentId && c.isUnread);
      if (rootUnread) return rootUnread.id;
    }
    return undefined;
  }, [commentThreads]);

  useEffect(() => {
    onUnreadCountChange?.(unreadCount);
  }, [unreadCount, onUnreadCountChange]);

  const onCommentSave = useCallback(
    (args: {
      message: string;
      files?: File[];
      commentId?: string;
      parentId?: string;
      edit?: boolean;
    }) => {
      saveComment({ ...args, parentId: args?.parentId ?? commentsThreadId });
    },
    [commentsThreadId, saveComment],
  );

  return (
    <Stack gap="0.5rem">
      {tabs && tabs.length > 1 && (
        <Box sx={{ paddingBottom: '1rem', marginLeft: '-0.75rem', width: 'calc(100% + 1.5rem)' }}>
          <Tabs
            value={Math.max(
              tabs.findIndex((tab) => tab.locationId === locationId),
              0,
            )}
            onChange={(_: SyntheticEvent, newValue: number) => {
              setLocationId?.(tabs[newValue].locationId);
            }}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            {tabs.map((tab) => (
              <Tab key={tab.commercialName} label={tab.commercialName} />
            ))}
          </Tabs>
        </Box>
      )}

      <CommentOrTaskForm
        type="comment"
        buttonLabel={t('Add Comment')}
        onTaskSave={onTaskSave}
        onCommentSave={onCommentSave}
      />

      {commentThreads ? (
        <CommentsList>
          {Object.entries(commentThreads)
            .reverse()
            .map(([key, comments]) => (
              <div key={key}>
                {comments
                  .filter((c: CommentWithUnread) =>
                    commentsThreadId ? c.id === commentsThreadId : !c.parentId,
                  )
                  .map((comment: CommentWithUnread) => (
                    <div key={comment.id}>
                      {comment.id === firstUnreadCommentId && <NewCommentsDivider />}
                      <Comment
                        comment={comment}
                        replies={comments.filter(
                          (c: CommentWithUnread) => c.parentId === comment.id,
                        )}
                        commentsThreadId={commentsThreadId}
                        onTaskSave={onTaskSave}
                        onCommentSave={onCommentSave}
                        deleteComment={deleteComment}
                        allUsers={allUsers?.content ?? []}
                        highlightCommentId={firstUnreadCommentId}
                        downloadCommentFile={downloadCommentFile}
                      />
                    </div>
                  ))}
              </div>
            ))}
        </CommentsList>
      ) : (
        <EmptyState variant={isLoading ? 'loading' : 'empty'} />
      )}
    </Stack>
  );
};

export default Comments;
